/**
 * Disclosure — a button that shows or hides a region below it (WAI-ARIA
 * disclosure pattern). The progressive-reveal primitive: "Advanced settings",
 * "Scan options", "More details", "Add a point by hand".
 *
 * - Controlled (`open` + `onOpenChange`) or uncontrolled (`defaultOpen`).
 * - `hint` is a one-line summary shown beside the label, so a collapsed
 *   section still says what it holds ("wlan0 · 10.2.10.0/24 · all protocols").
 * - Closed content is not rendered unless `keepMounted`, which keeps it in the
 *   DOM under `hidden` (for form state that must survive a collapse).
 * - `variant="card"` draws a bordered box; `"plain"` (default) is a text
 *   toggle for inline use; `"link"` is a quiet brand-coloured link.
 *
 * No Radix dependency: a native button with aria-expanded / aria-controls is
 * the whole pattern, and it renders under renderToStaticMarkup in tests.
 */

import { useId, useState, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "./cn";

export interface DisclosureProps {
  /** The toggle's label. */
  summary: ReactNode;
  /** A one-line summary shown after the label (collapsed or not). */
  hint?: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Keep the content mounted (under `hidden`) while closed. */
  keepMounted?: boolean;
  variant?: "plain" | "card" | "link";
  /** Extra controls at the right of the toggle row (not part of the button). */
  actions?: ReactNode;
  className?: string;
  contentClassName?: string;
  children: ReactNode;
}

export function Disclosure({
  summary,
  hint,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  keepMounted = false,
  variant = "plain",
  actions,
  className,
  contentClassName,
  children,
}: DisclosureProps) {
  const [openState, setOpenState] = useState(defaultOpen);
  const open = openProp ?? openState;
  const contentId = useId();
  const toggle = () => {
    const next = !open;
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  return (
    <div
      className={cn(
        "flex flex-col",
        variant === "card" && "rounded-lg border border-border bg-surface-elevated",
        className,
      )}
    >
      <div className={cn("flex items-center gap-2", variant === "card" && "px-4 py-3")}>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={open || keepMounted ? contentId : undefined}
          onClick={toggle}
          className={cn(
            "group flex min-w-0 flex-1 items-center gap-1.5 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            variant === "link"
              ? "text-xs font-medium text-brand hover:underline"
              : "text-sm font-medium text-on-surface",
          )}
        >
          <ChevronRight
            aria-hidden
            className={cn(
              "size-4 shrink-0 text-on-surface-muted transition-transform",
              open && "rotate-90",
              variant === "link" && "size-3.5 text-brand",
            )}
          />
          <span className="shrink-0">{summary}</span>
          {hint && (
            <span className="min-w-0 truncate text-xs font-normal text-on-surface-muted">{hint}</span>
          )}
        </button>
        {actions && <div className="flex shrink-0 items-center gap-1">{actions}</div>}
      </div>
      {(open || keepMounted) && (
        <div
          id={contentId}
          hidden={!open}
          className={cn(
            variant === "card" ? "border-t border-border px-4 py-3" : "pt-3",
            contentClassName,
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}
